import React, { useEffect, useState } from 'react';
import {
  Box, Drawer, Stack, Typography, Chip, Divider, Avatar,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { LoadingOverlay, EmptyState, Code, GlassIconButton } from 'dashboard/components/common';

const ACTION_LABEL = {
  'user.enable': 'Enabled user',
  'user.disable': 'Disabled user',
  'user.password_reset': 'Sent password reset',
  'brand.enable': 'Enabled brand',
  'brand.disable': 'Disabled brand',
  'sub.cancel': 'Cancelled subscription',
  'sub.cancel.immediate': 'Cancelled (immediate)',
  'sub.resume': 'Resumed subscription',
  'sub.refund.full': 'Refunded (full)',
  'sub.refund.partial': 'Refunded (partial)',
  'domain.add': 'Added domain',
  'domain.delete': 'Deleted domain',
  'domain.verify.success': 'Domain verified',
  'domain.verify.fail': 'Domain verify failed',
};

const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString() : '—');

/**
 * Right-side drawer with everything we know about one user.
 *
 * Lazily fetches on open so flipping users in the table doesn't
 * fan out per-row XHRs. The data shape matches the
 * `getUserDetails` aggregator on the backend.
 */
export default function UserDetailsDrawer({ userId, onClose }) {
  const open = !!userId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) { setData(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    API.adminUserDetails({ _id: userId })
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load user details'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const u = data?.user;

  return (
    <Drawer anchor='right' open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 480 }, p: '20px' }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' mb='14px'>
          <Typography variant='h6' fontWeight='700'>User details</Typography>
          <GlassIconButton size='small' label='Close' onClick={onClose}>
            <CloseIcon fontSize='small' />
          </GlassIconButton>
        </Stack>

        <LoadingOverlay loading={loading && !data} minHeight={240}>
          {u && (
            <>
              <Stack direction='row' spacing='12px' alignItems='center' mb='12px'>
                <Avatar sx={{ width: 48, height: 48 }}>
                  {(u.email || '?').slice(0, 1).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight='700' noWrap>{u.email}</Typography>
                  <Typography variant='caption' color='text.secondary' noWrap>
                    {u.username}{u.is_active === false ? ' · disabled' : ''}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction='row' spacing='6px' flexWrap='wrap' useFlexGap mb='14px'>
                {(u.roles || []).map((r) => (
                  <Chip key={r} size='small' label={r} />
                ))}
                {u.has_stripe && <Chip size='small' color='success' label='Stripe customer' />}
                {u.google_id && <Chip size='small' variant='outlined' label='Google' />}
                {u.github_id && <Chip size='small' variant='outlined' label='GitHub' />}
                {u.facebook_id && <Chip size='small' variant='outlined' label='Facebook' />}
                {u.is_deleted && <Chip size='small' color='error' label='Deleted' />}
              </Stack>

              <Typography variant='caption' color='text.secondary' display='block' mb='14px'>
                Joined {fmtTime(u.createdAt)} · ID <Code>{String(u._id)}</Code>
              </Typography>

              <Divider sx={{ my: '14px' }} />

              <Typography variant='caption' color='text.secondary' textTransform='uppercase' fontWeight='700' display='block' mb='8px'>
                Owned brands ({data.owned_brands?.length || 0})
              </Typography>
              {data.owned_brands?.length ? (
                <Stack spacing='6px' mb='10px'>
                  {data.owned_brands.map((b) => (
                    <Paper key={b._id} elevation={0} sx={{ p: '10px', border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                      <Stack direction='row' justifyContent='space-between' alignItems='center'>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant='body2' fontWeight='600' noWrap>
                            {b.name}{b.is_active === false ? ' (disabled)' : ''}
                          </Typography>
                          {b.domain && <Typography variant='caption' color='text.secondary'>{b.domain}</Typography>}
                        </Box>
                        {b.is_active === false && <Chip size='small' color='error' label='Disabled' />}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <EmptyState>No brands owned by this user.</EmptyState>
              )}

              {(data.member_brands?.length || 0) > 0 && (
                <>
                  <Typography variant='caption' color='text.secondary' textTransform='uppercase' fontWeight='700' display='block' mt='14px' mb='8px'>
                    Member of ({data.member_brands.length})
                  </Typography>
                  <Stack direction='row' spacing='6px' flexWrap='wrap' useFlexGap mb='6px'>
                    {data.member_brands.map((b) => (
                      <Chip key={b._id} size='small' variant='outlined' label={b.name} />
                    ))}
                  </Stack>
                </>
              )}

              <Divider sx={{ my: '14px' }} />

              <Typography variant='caption' color='text.secondary' textTransform='uppercase' fontWeight='700' display='block' mb='8px'>
                Recent activity ({data.recent_audit?.length || 0})
              </Typography>
              {data.recent_audit?.length ? (
                <Stack spacing='6px'>
                  {data.recent_audit.map((e) => (
                    <Paper key={e._id} elevation={0} sx={{ p: '10px', border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                      <Stack direction='row' justifyContent='space-between' alignItems='center' spacing='8px'>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant='body2' noWrap>
                            <strong>{ACTION_LABEL[e.action] || e.action}</strong>
                            {e.target_label && e.target_label !== u.email ? ` · ${e.target_label}` : ''}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {String(e.actor_user_id) === String(u._id) ? 'by them' : `by ${e.actor_email || 'admin'}`}
                          </Typography>
                        </Box>
                        <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                          {fmtTime(e.createdAt)}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <EmptyState>No recorded admin activity for this user.</EmptyState>
              )}
            </>
          )}
        </LoadingOverlay>
      </Box>
    </Drawer>
  );
}
